import { Modal } from "@hyperhedge/ui";
import { PolymarketClient } from "@hyperhedge/polymarket";
import { useQuery } from "@tanstack/react-query";
import type { CSSProperties } from "react";
import { useMemo, useState } from "react";
import type { SelectedPred } from "../types";

// Route through our Pages Function proxy (prod) / Vite dev proxy (local).
// Polymarket's Gamma API does not send CORS headers, so the browser can't
// hit it directly — mirroring ea_ccda's `functions/api/*` proxy pattern.
const client = new PolymarketClient({
  gammaUrl: `${window.location.origin}/api/polymarket`,
  clobUrl: `${window.location.origin}/api/polymarket`,
});

type PMMarket = Awaited<ReturnType<PolymarketClient["listMarkets"]>>[number];

function parseJsonArray(s: string | undefined): string[] {
  if (!s) return [];
  try {
    const v = JSON.parse(s) as unknown;
    return Array.isArray(v) ? v.map((x) => String(x)) : [];
  } catch {
    return [];
  }
}

function pricesFor(m: PMMarket): { yes?: number; no?: number } {
  const outcomes = parseJsonArray(m.outcomes);
  const prices = parseJsonArray(m.outcomePrices).map((p) => Number(p));
  const yesIdx = outcomes.findIndex((o) => o.toLowerCase() === "yes");
  if (yesIdx >= 0) {
    return { yes: prices[yesIdx], no: prices[1 - yesIdx] };
  }
  return { yes: prices[0], no: prices[1] };
}

function toSelected(m: PMMarket): SelectedPred {
  const { yes, no } = pricesFor(m);
  const outcomes = parseJsonArray(m.outcomes);
  const tokenIds = parseJsonArray(m.clobTokenIds);
  const yesIdx = outcomes.findIndex((o) => o.toLowerCase() === "yes");
  const yesTokenId =
    yesIdx >= 0 ? tokenIds[yesIdx] : tokenIds[0];
  return {
    id: m.id,
    conditionId: m.conditionId,
    clobTokenIds: tokenIds,
    yesTokenId,
    question: m.question,
    image: m.image,
    yesPrice: yes,
    noPrice: no,
    volume24hr: Number(m.volume24hr ?? 0),
    liquidity: Number(m.liquidity ?? 0),
    endDate: m.endDate,
    category: m.category,
    negRisk: m.negRisk ?? false,
  };
}

function formatUsd(n: number): string {
  if (!isFinite(n) || n === 0) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

function formatEnd(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const days = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return "ENDED";
    if (days === 0) return "0d";
    if (days === 1) return "1d";
    if (days < 365) return `${days}d`;
    return `${(days / 365).toFixed(1)}y`;
  } catch {
    return "—";
  }
}

// Hash a string into a hue (0–360). Stable per-category tint.
function hueFor(s: string | undefined): number {
  if (!s) return 200;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

export function PolymarketMarketsModal({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (m: SelectedPred) => void;
}) {
  const [filter, setFilter] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["pm", "list-markets"],
    queryFn: () => client.listMarkets({ limit: 200, closed: false, order: "volume24hr" }),
    enabled: open,
    staleTime: 60_000,
  });

  const rows = useMemo(() => {
    if (!data) return [] as PMMarket[];
    const q = filter.trim().toLowerCase();
    const matches = q
      ? data.filter((m) => m.question.toLowerCase().includes(q))
      : data;
    return matches.slice(0, 500);
  }, [data, filter]);

  const maxVol = useMemo(
    () => rows.reduce((mx, m) => Math.max(mx, Number(m.volume24hr ?? 0)), 1),
    [rows],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="POLYMARKET MARKETS"
      subtitle={
        data ? `${rows.length} OF ${data.length} ACTIVE · SORT: 24H VOLUME` : "LOADING"
      }
      width="min(1200px, 95vw)"
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
    >
      {/* SEARCH */}
      <div
        style={{
          height: 34,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 14px",
          borderBottom: "1px solid var(--line-2)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            color: "var(--fg-meta)",
            letterSpacing: "0.14em",
          }}
        >
          ⌕
        </span>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="QUESTION · KEYWORD"
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--fg-hi)",
            fontFamily: "var(--f-mono)",
            fontSize: 11,
            letterSpacing: "0.08em",
          }}
        />
      </div>

      {/* LIST */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {isLoading && (
          <div
            style={{
              padding: 48,
              textAlign: "center",
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--fg-meta)",
            }}
          >
            LOADING MARKETS…
          </div>
        )}
        {error && (
          <div
            style={{
              padding: 48,
              textAlign: "center",
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              color: "var(--sev-crit)",
            }}
          >
            FAILED TO LOAD — {String(error)}
          </div>
        )}
        {!isLoading &&
          !error &&
          rows.map((m, i) => (
            <MarketRow
              key={m.id}
              market={m}
              zebra={i % 2 === 1}
              volMax={maxVol}
              onClick={() => {
                onSelect(toSelected(m));
                onClose();
              }}
            />
          ))}
        {!isLoading && !error && rows.length === 0 && (
          <div
            style={{
              padding: 48,
              textAlign: "center",
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              color: "var(--fg-meta)",
            }}
          >
            NO MATCHES
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div
        style={{
          height: 26,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 14px",
          borderTop: "1px solid var(--line-2)",
          fontFamily: "var(--f-mono)",
          fontSize: 9,
          letterSpacing: "0.16em",
          color: "var(--fg-dim)",
          textTransform: "uppercase",
        }}
      >
        {data ? `${rows.length} / ${data.length}` : ""}
      </div>
    </Modal>
  );
}

const ROW_HEIGHT = 36;

const rowBase: CSSProperties = {
  position: "relative",
  height: ROW_HEIGHT,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "0 12px 0 14px",
  borderBottom: "1px solid var(--line-1)",
  cursor: "pointer",
  textAlign: "left",
  width: "100%",
};

function MarketRow({
  market,
  zebra,
  volMax,
  onClick,
}: {
  market: PMMarket;
  zebra: boolean;
  volMax: number;
  onClick: () => void;
}) {
  const { yes, no } = pricesFor(market);
  const volume = Number(market.volume24hr ?? 0);
  const volPct = Math.min(100, (volume / volMax) * 100);
  const tintHue = hueFor(market.category ?? market.id);
  const leftEdge = `hsla(${tintHue}, 60%, 70%, 0.45)`;
  const bg = zebra ? "var(--bg-2)" : "var(--bg-1)";

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...rowBase,
        background: bg,
        boxShadow: `inset 2px 0 0 ${leftEdge}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--bg-3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = bg;
      }}
    >
      {/* IMAGE */}
      {market.image ? (
        <img
          src={market.image}
          alt=""
          style={{
            width: 20,
            height: 20,
            flexShrink: 0,
            objectFit: "cover",
            border: "1px solid var(--line-2)",
            borderRadius: 2,
          }}
        />
      ) : (
        <div
          style={{
            width: 20,
            height: 20,
            flexShrink: 0,
            border: "1px solid var(--line-2)",
            background: `hsla(${tintHue}, 40%, 25%, 0.3)`,
            borderRadius: 2,
          }}
        />
      )}

      {/* YES / NO CHIPS */}
      <div
        style={{
          display: "flex",
          gap: 4,
          flexShrink: 0,
          fontFamily: "var(--f-mono)",
          fontSize: 10,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.04em",
        }}
      >
        <span
          style={{
            color: "var(--up)",
            padding: "2px 6px",
            border: "1px solid rgba(108, 207, 142, 0.3)",
            background: "rgba(108, 207, 142, 0.08)",
            borderRadius: 2,
            minWidth: 44,
            textAlign: "center",
          }}
        >
          Y {yes !== undefined ? `${(yes * 100).toFixed(0)}¢` : "—"}
        </span>
        <span
          style={{
            color: "var(--dn)",
            padding: "2px 6px",
            border: "1px solid rgba(224, 106, 106, 0.3)",
            background: "rgba(224, 106, 106, 0.08)",
            borderRadius: 2,
            minWidth: 44,
            textAlign: "center",
          }}
        >
          N {no !== undefined ? `${(no * 100).toFixed(0)}¢` : "—"}
        </span>
      </div>

      {/* QUESTION */}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: "var(--f-sans)",
          fontSize: 12,
          color: "var(--fg-hi)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: 1.3,
        }}
        title={market.question}
      >
        {market.question}
      </span>

      {/* VOL BAR */}
      <div
        style={{
          position: "relative",
          width: 110,
          height: 18,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          paddingRight: 4,
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 3,
            bottom: 3,
            right: 0,
            width: `${volPct}%`,
            background: "rgba(140, 160, 180, 0.15)",
            borderRadius: 1,
          }}
        />
        <span
          style={{
            position: "relative",
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            color: "var(--fg-meta)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatUsd(volume)}
        </span>
      </div>

      {/* ENDS */}
      <span
        style={{
          flexShrink: 0,
          width: 44,
          textAlign: "right",
          fontFamily: "var(--f-mono)",
          fontSize: 10,
          color: "var(--fg-dim)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatEnd(market.endDate)}
      </span>
    </button>
  );
}
